export const PANEL_TYPES = ['chart', 'stat', 'gauge', 'faults', 'live'] as const;
export type PanelType = (typeof PANEL_TYPES)[number];

export const LIVE_FIELDS = [
  'joint_temperatures', 'joint_positions', 'cycle_time_ms',
  'pick_success_rate', 'power_watts', 'cpu_temp',
] as const;
export type LiveField = (typeof LIVE_FIELDS)[number];

export interface PanelBase { id: string; title: string; x: number; y: number; w: number; h: number; }

export type ChartPanelConfig  = PanelBase & { type: 'chart';  metric: string; serials: string[] };
export type StatPanelConfig   = PanelBase & { type: 'stat';   metric: string; serials: string[]; options?: { reduce?: 'last' | 'avg' | 'max' } };
export type GaugePanelConfig  = PanelBase & { type: 'gauge';  metric: string; serials: string[]; options?: { min?: number; max?: number } };
export type FaultsPanelConfig = PanelBase & { type: 'faults'; serials: string[] };
export type LivePanelConfig   = PanelBase & { type: 'live';   serial: string; field: LiveField };

export type Panel = ChartPanelConfig | StatPanelConfig | GaugePanelConfig | FaultsPanelConfig | LivePanelConfig;

export type DashboardScope = 'user' | 'org';

export interface DashboardDoc {
  v: 1;
  name: string;
  scope: DashboardScope;
  timeRange: string;
  refresh: number; // seconds, 0 = off
  panels: Panel[];
}

// Wire shapes from the server (storage keys are never sent over the wire).
export interface DashboardRecord extends DashboardDoc {
  id: string;
  created_by: string;
  updated_at: string;
}
export interface DashboardSummary {
  id: string;
  name: string;
  scope: DashboardScope;
  updated_at: string;
  created_by: string;
}

export function isDashboardDoc(x: unknown): x is DashboardDoc {
  if (typeof x !== 'object' || x === null) return false;
  const d = x as Record<string, unknown>;
  return d['v'] === 1
    && typeof d['name'] === 'string'
    && (d['scope'] === 'user' || d['scope'] === 'org')
    && typeof d['timeRange'] === 'string'
    && typeof d['refresh'] === 'number'
    && Array.isArray(d['panels'])
    && (d['panels'] as unknown[]).every((p) =>
      typeof p === 'object' && p !== null
      && typeof (p as Panel)['id'] === 'string'
      && PANEL_TYPES.includes((p as Panel)['type']));
}
